import type { EmailAuthResult } from "../api/email";
import type { User } from "@/shared/lib/context/UserContext";
import {
    getAccountIdentity,
    resetIdentityCache,
} from "@/features/identity/api/identityApi";
import { isPlaceholderName } from "@/shared/lib/isPlaceholderName";
import { activityLog } from "@/features/activityLog";
import type { LoginMethod } from "@/features/activityLog";

const R2_WORKER_URL = "https://bucket.torsunovoleggen.workers.dev";
const PERSONAL_DATA_WORKER_URL =
    "https://personal-data-worker.torsunovoleggen.workers.dev";

export interface StoreSessionContext {
    /** called to update the React user context on success */
    setUser?: (user: User | string) => void;
    /** How the person came in, for the activity log (a code from the app is not an email login). */
    loginMethod?: LoginMethod;
    /**
     * Asked before each write, once the workers have answered: false when
     * the login this step finishes is no longer the browser's — the person
     * signed out, or in as someone else, while the workers were slow. Then
     * nothing is written and the React user is left alone: a late answer
     * must not sign anybody back in. For a login that finishes behind a page
     * already shown (a code from the app, webHandoff.ts); the login pages
     * wait for this step and need no guard.
     */
    stillCurrent?: () => boolean;
    /** A worker that has not answered in this many ms is given up (missing, as when it fails). */
    workerTimeoutMs?: number;
}

/**
 * Given an email_id_token (short-lived HS256 JWT from site_server),
 * fetches r2_session_token from the bucket worker and
 * personal_data_session_token from the personal-data-worker in parallel,
 * then writes everything to localStorage and updates the user context.
 *
 * Mirrors what `GoogleCallbackPage.handleAuth` and `LoginPage.handleAppleAuth`
 * already do for Google id_tokens and Apple id_tokens. Any individual worker
 * call is best-effort: on failure we console.warn and continue, matching the
 * resilience of the existing OAuth flows.
 */
export async function finalizeEmailAuth(
    result: EmailAuthResult,
    ctx: StoreSessionContext = {},
): Promise<void> {
    const { email_id_token, site_session_token, email, name } = result;
    const overtaken = () => ctx.stillCurrent?.() === false;

    const ctrl = ctx.workerTimeoutMs ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), ctx.workerTimeoutMs) : undefined;
    const [r2Response, personalDataResponse] = await Promise.allSettled([
        fetch(`${R2_WORKER_URL}/auth/email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_token: email_id_token }),
            signal: ctrl?.signal,
        }),
        fetch(`${PERSONAL_DATA_WORKER_URL}/auth/email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_token: email_id_token }),
            signal: ctrl?.signal,
        }),
    ]);
    // Both have answered (or were given up); the answers are a few bytes.
    clearTimeout(timer);

    if (r2Response.status === "fulfilled" && r2Response.value.ok) {
        const r2Data = (await r2Response.value.json()) as {
            session_token?: string;
        };
        if (overtaken()) return;
        if (r2Data.session_token) {
            localStorage.setItem("r2_session_token", r2Data.session_token);
        }
    } else {
        const err =
            r2Response.status === "rejected"
                ? r2Response.reason
                : await r2Response.value.text().catch(() => "");
        console.warn("R2 Worker /auth/email failed:", err);
    }

    if (
        personalDataResponse.status === "fulfilled" &&
        personalDataResponse.value.ok
    ) {
        const pdData = (await personalDataResponse.value.json()) as {
            session_token?: string;
        };
        if (overtaken()) return;
        if (pdData.session_token) {
            localStorage.setItem(
                "personal_data_session_token",
                pdData.session_token,
            );
            // Mirror to legacy `auth_token` so the questions/credits flow,
            // askedQuestionsHistory, share-credits modal and other older
            // readers see the session without each having to add a
            // personal_data fallback.
            localStorage.setItem("auth_token", pdData.session_token);
        }
    } else {
        const err =
            personalDataResponse.status === "rejected"
                ? personalDataResponse.reason
                : await personalDataResponse.value.text().catch(() => "");
        console.warn("Personal Data Worker /auth/email failed:", err);
    }

    if (overtaken()) return;
    // site_session_token comes directly from site_server, no extra call needed
    localStorage.setItem("site_session_token", site_session_token);
    localStorage.setItem("site_user_email", email);
    localStorage.setItem("site_user_name", name || "");
    // Cache the email_id_token so the boot-time `ensureR2SessionToken`
    // hook in features/bucket/api/index.ts can silently re-issue
    // r2_session_token if it's missing or invalidated later. Without
    // this, partial-login states (where r2 fetch failed at login time)
    // produce 403 spam on lecture-sync endpoints until the user does
    // a full re-login.
    localStorage.setItem("email_id_token", email_id_token);

    // An email login knows nothing but the address, so a person who first
    // signed in with Google (on the phone, say) would lose their name and
    // their face by coming back through the email door. The worker kept both
    // in identity.json; ask for them now that the tokens are in place.
    //
    // Only ever FILLS IN what is missing — the site server's own name wins —
    // and never fails the login: identity is decoration on a valid session.
    let identityName = "";
    let identityPicture = "";
    try {
        resetIdentityCache(); // this tab may still hold the previous person
        const identity = await getAccountIdentity();
        if (identity) {
            identityName = identity.name || "";
            // Google's own URL, not the worker's /account/photo: it is stored
            // and re-read across reloads, and /account/photo needs a Bearer
            // header that an <img> cannot send.
            identityPicture = identity.googlePicture || "";
        }
    } catch {
        // best effort — the session is already good without it
    }
    if (overtaken()) return;

    if (identityName && isPlaceholderName(localStorage.getItem("site_user_name"))) {
        localStorage.setItem("site_user_name", identityName);
    }
    if (identityPicture && !localStorage.getItem("site_user_picture")) {
        localStorage.setItem("site_user_picture", identityPicture);
    }

    activityLog.hintLogin(ctx.loginMethod ?? "email");
    // UserContext.User requires `id: string` — use the email as the stable id
    // (consistent with the uid `email:{email}` written to KV by the bucket worker).
    ctx.setUser?.({
        id: `email:${email}`,
        email,
        name: name || identityName || undefined,
        picture: identityPicture || undefined,
    });
}
